/* eslint-disable local/no-hex-color-literals -- ADR-006 §excepción:
 * ilustración SVG de marca. Las paradas de gradiente y los tintes de acuarela
 * no son tokens del design system: son pigmentos de una obra, no colores de UI.
 * Deuda registrada: exponerlos como `--art-*` en tokens.css y consumirlos vía
 * `style={{ fill: var(--art-*) }}` cuando se haga el pase de design tokens. */
/**
 * SpiritualSymbol.tsx — sistema de símbolos espirituales premium.
 *
 * 9 variantes SVG basadas fielmente en `docs/reference/referencia-simbolos-
 * espirituales.png` (material de diseño; no se sirve al browser):
 *
 *   · eye        → PROTECCIÓN — ojo egipcio con iris + rayos radiales.
 *   · moon       → INTUICIÓN — luna creciente ornamental + estrella + puntos.
 *   · lotus      → SANACIÓN — loto de 5 pétalos con acuarela lavanda+dorado.
 *   · sun        → EXPANSIÓN — sol con rayos radiales + estrella central.
 *   · orbit      → CONEXIÓN — 3 órbitas concéntricas con planetas + puntos.
 *   · butterfly  → TRANSFORMACIÓN — mariposa con alas ornamentales.
 *   · roots      → RAÍCES — árbol con raíces expandiéndose desde el tronco.
 *   · star       → GUÍA — estrella de 8 puntas con destello.
 *   · spiral     → ALMA — círculo con dos espirales opuestas + puntos.
 *
 * Reemplaza a los iconos brand simples usados como placeholder cuando un
 * item (obra, producto, evento) no tiene imagen real. Preservan el
 * lenguaje visual premium del proyecto sobre CUALQUIER fondo (crema o
 * violeta) gracias a la prop `tone`.
 *
 * Uso:
 *   <SpiritualSymbol variant="lotus" size={72} tone="dark" />  // sobre violeta
 *   <SpiritualSymbol variant="eye"   size={72} tone="light" /> // sobre crema
 *
 * Asignación estable:
 *   <SpiritualSymbol variant={pickSymbol(item.slug, item.title)} .../>
 */
import type { ReactElement } from 'react';

// ---------------------------------------------------------------------------
// Paleta interna (hex dentro de atributos SVG — permitido: el linter del
// proyecto apunta a strings de tokens Tailwind/CSS, no a atributos SVG).
// ---------------------------------------------------------------------------

interface Palette {
  primary: string;
  secondary: string;
  accent: string;
  accentSoft: string;
}

const PALETTE_LIGHT: Palette = {
  primary: '#B8933E', // dorado profundo — línea principal sobre crema
  secondary: '#D8B871', // dorado suave — acentos
  accent: '#553087', // lavanda profundo — texturas acuarela
  accentSoft: '#8A66C4', // lavanda claro — bordes acuarela
};

const PALETTE_DARK: Palette = {
  primary: '#E0B874', // dorado brillante — línea principal sobre violeta
  secondary: '#F0E6D0', // crema — acentos que reemplazan al lavanda
  accent: '#F5E9CE', // crema con calidez — reemplaza al lavanda profundo
  accentSoft: '#EBD9AA', // dorado tenue — reemplaza al lavanda claro
};

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export type SpiritualSymbolVariant =
  | 'eye'
  | 'moon'
  | 'lotus'
  | 'sun'
  | 'orbit'
  | 'butterfly'
  | 'roots'
  | 'star'
  | 'spiral';

export interface SpiritualSymbolProps {
  variant: SpiritualSymbolVariant;
  /** Tamaño total (ancho == alto). Default 72px. */
  size?: number;
  /** Tono adaptado al fondo. `dark` → sobre violeta; `light` → sobre crema. */
  tone?: 'light' | 'dark';
  /** ClassName extra para posicionamiento. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Variantes SVG — todas con viewBox 200×200 para uniformidad
// ---------------------------------------------------------------------------

function EyeSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Rayos superiores */}
      <g stroke={p.primary} strokeWidth="1.4" strokeLinecap="round">
        <line x1="100" y1="34" x2="100" y2="50" />
        <line x1="82" y1="42" x2="86" y2="54" />
        <line x1="118" y1="42" x2="114" y2="54" />
        <line x1="70" y1="56" x2="76" y2="66" />
        <line x1="130" y1="56" x2="124" y2="66" />
      </g>
      {/* Rayos inferiores */}
      <g stroke={p.primary} strokeWidth="1.4" strokeLinecap="round">
        <line x1="100" y1="150" x2="100" y2="166" />
        <line x1="82" y1="158" x2="86" y2="146" />
        <line x1="118" y1="158" x2="114" y2="146" />
      </g>
      {/* Almendra del ojo — contorno dorado */}
      <path
        d="M40 100 Q 100 60, 160 100 Q 100 140, 40 100 Z"
        fill="none"
        stroke={p.primary}
        strokeWidth="2.2"
      />
      {/* Segunda línea interna */}
      <path
        d="M50 100 Q 100 68, 150 100 Q 100 132, 50 100 Z"
        fill="none"
        stroke={p.primary}
        strokeWidth="0.9"
        opacity="0.6"
      />
      {/* Iris circular con acuarela */}
      <circle cx="100" cy="100" r="18" fill={p.accentSoft} opacity="0.55" />
      <circle cx="100" cy="100" r="16" fill={p.accent} opacity="0.75" />
      {/* Pupila */}
      <circle cx="100" cy="100" r="6" fill={p.primary} />
      {/* Destellos internos */}
      <circle cx="96" cy="96" r="1.5" fill={p.secondary} opacity="0.9" />
      <circle cx="104" cy="103" r="1" fill={p.secondary} opacity="0.7" />
    </>
  );
}

function MoonSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Luna creciente rellena */}
      <defs>
        <linearGradient id="moon-symbol-fill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={p.secondary} />
          <stop offset="100%" stopColor={p.primary} />
        </linearGradient>
      </defs>
      <path
        d="M110 40 A 60 60 0 1 0 130 150 A 46 46 0 0 1 110 40 Z"
        fill="url(#moon-symbol-fill)"
        stroke={p.primary}
        strokeWidth="1.2"
      />
      {/* Textura ornamental en la luna */}
      <path
        d="M78 80 Q 90 90 82 110"
        fill="none"
        stroke={p.primary}
        strokeWidth="0.8"
        opacity="0.7"
      />
      <circle cx="90" cy="70" r="1.5" fill={p.primary} opacity="0.7" />
      <circle cx="76" cy="100" r="1.5" fill={p.primary} opacity="0.7" />
      <circle cx="88" cy="130" r="1.5" fill={p.primary} opacity="0.7" />
      {/* Estrella brillante grande */}
      <g fill={p.primary}>
        <path d="M145 68 L 148 76 L 156 78 L 148 80 L 145 88 L 142 80 L 134 78 L 142 76 Z" />
        <circle cx="145" cy="78" r="2.5" fill={p.secondary} />
      </g>
      {/* Estrella pequeña */}
      <g fill={p.primary} opacity="0.85">
        <path d="M165 55 L 166 60 L 171 61 L 166 62 L 165 67 L 164 62 L 159 61 L 164 60 Z" />
      </g>
      {/* Puntos dispersos */}
      <circle cx="155" cy="98" r="1.8" fill={p.primary} opacity="0.8" />
      <circle cx="170" cy="110" r="1.2" fill={p.primary} opacity="0.65" />
      <circle cx="148" cy="130" r="1.2" fill={p.primary} opacity="0.7" />
      <circle cx="130" cy="55" r="1" fill={p.primary} opacity="0.6" />
    </>
  );
}

function LotusSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Pétalos exteriores más claros */}
      <path
        d="M100 150 Q 55 130 40 90 Q 60 88 78 100 Q 90 118 100 150 Z"
        fill={p.accentSoft}
        opacity="0.42"
        stroke={p.accent}
        strokeWidth="0.8"
      />
      <path
        d="M100 150 Q 145 130 160 90 Q 140 88 122 100 Q 110 118 100 150 Z"
        fill={p.accentSoft}
        opacity="0.42"
        stroke={p.accent}
        strokeWidth="0.8"
      />
      {/* Pétalos medios */}
      <path
        d="M100 150 Q 68 120 66 70 Q 82 76 92 92 Q 100 118 100 150 Z"
        fill={p.accent}
        opacity="0.55"
        stroke={p.primary}
        strokeWidth="0.9"
      />
      <path
        d="M100 150 Q 132 120 134 70 Q 118 76 108 92 Q 100 118 100 150 Z"
        fill={p.accent}
        opacity="0.55"
        stroke={p.primary}
        strokeWidth="0.9"
      />
      {/* Pétalo central grande con contorno dorado */}
      <path
        d="M100 152 Q 88 110 96 55 Q 100 48 104 55 Q 112 110 100 152 Z"
        fill={p.secondary}
        opacity="0.65"
        stroke={p.primary}
        strokeWidth="1.2"
      />
      {/* Base horizontal sutil */}
      <path
        d="M50 152 Q 100 160 150 152"
        fill="none"
        stroke={p.primary}
        strokeWidth="0.8"
        opacity="0.55"
      />
      {/* Estrellas superiores */}
      <g fill={p.primary}>
        <path d="M60 60 L 61 64 L 65 65 L 61 66 L 60 70 L 59 66 L 55 65 L 59 64 Z" opacity="0.85" />
        <path
          d="M140 55 L 141 59 L 145 60 L 141 61 L 140 65 L 139 61 L 135 60 L 139 59 Z"
          opacity="0.85"
        />
        <circle cx="100" cy="42" r="2.5" />
        <circle cx="100" cy="42" r="1.2" fill={p.secondary} />
      </g>
      <circle cx="80" cy="52" r="1.2" fill={p.primary} opacity="0.7" />
      <circle cx="120" cy="48" r="1.2" fill={p.primary} opacity="0.7" />
    </>
  );
}

function SunSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* 16 rayos radiales */}
      <g stroke={p.primary} strokeWidth="1.4" strokeLinecap="round">
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i * 22.5 * Math.PI) / 180;
          const inner = 55;
          const outer = i % 2 === 0 ? 90 : 78;
          const x1 = 100 + inner * Math.cos(angle);
          const y1 = 100 + inner * Math.sin(angle);
          const x2 = 100 + outer * Math.cos(angle);
          const y2 = 100 + outer * Math.sin(angle);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} opacity={i % 2 === 0 ? 1 : 0.65} />;
        })}
      </g>
      {/* Puntitos entre rayos largos */}
      <g fill={p.primary}>
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = ((i * 45 + 22.5) * Math.PI) / 180;
          const r = 82;
          return (
            <circle
              key={i}
              cx={100 + r * Math.cos(angle)}
              cy={100 + r * Math.sin(angle)}
              r="1.6"
              opacity="0.85"
            />
          );
        })}
      </g>
      {/* Círculo central */}
      <circle cx="100" cy="100" r="30" fill="none" stroke={p.primary} strokeWidth="2" />
      <circle
        cx="100"
        cy="100"
        r="26"
        fill="none"
        stroke={p.primary}
        strokeWidth="0.7"
        opacity="0.55"
      />
      {/* Estrella central */}
      <g fill={p.primary}>
        <path d="M100 88 L 102 96 L 110 100 L 102 104 L 100 112 L 98 104 L 90 100 L 98 96 Z" />
        <circle cx="100" cy="100" r="2" fill={p.secondary} />
      </g>
    </>
  );
}

function OrbitSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Órbita exterior */}
      <circle
        cx="100"
        cy="100"
        r="72"
        fill="none"
        stroke={p.primary}
        strokeWidth="0.9"
        strokeDasharray="0.5 3"
      />
      <ellipse cx="100" cy="100" rx="72" ry="70" fill="none" stroke={p.primary} strokeWidth="1.3" />
      {/* Órbita media */}
      <circle cx="100" cy="100" r="48" fill="none" stroke={p.primary} strokeWidth="1" />
      {/* Órbita interior */}
      <circle cx="100" cy="100" r="24" fill="none" stroke={p.primary} strokeWidth="1" />
      {/* Planeta central */}
      <circle cx="100" cy="100" r="6" fill={p.primary} />
      <circle cx="100" cy="100" r="3" fill={p.secondary} />
      {/* Puntos orbitando (planetas pequeños) */}
      <g fill={p.primary}>
        {/* Interior */}
        <circle cx="124" cy="100" r="2.5" />
        <circle cx="76" cy="100" r="2.2" opacity="0.85" />
        {/* Media */}
        <circle cx="100" cy="52" r="3.2" />
        <circle cx="130" cy="132" r="2.5" opacity="0.85" />
        <circle cx="66" cy="115" r="2" opacity="0.75" />
        {/* Exterior */}
        <circle cx="172" cy="100" r="3.5" />
        <circle cx="28" cy="100" r="3.5" />
        <circle cx="100" cy="28" r="2.5" opacity="0.85" />
        <circle cx="100" cy="172" r="3" opacity="0.85" />
        <circle cx="150" cy="46" r="2.2" opacity="0.75" />
      </g>
    </>
  );
}

function ButterflySymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Cuerpo central */}
      <ellipse cx="100" cy="102" rx="3.5" ry="30" fill={p.primary} />
      {/* Antenas */}
      <path
        d="M97 72 Q 88 55 78 50"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M103 72 Q 112 55 122 50"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="76" cy="49" r="2" fill={p.primary} />
      <circle cx="124" cy="49" r="2" fill={p.primary} />
      {/* Ala superior izquierda */}
      <path
        d="M96 84 Q 60 62 32 78 Q 24 100 40 110 Q 62 106 96 96 Z"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.6"
      />
      {/* Ala inferior izquierda */}
      <path
        d="M96 106 Q 66 112 46 130 Q 42 146 60 148 Q 82 142 96 122 Z"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.6"
      />
      {/* Ala superior derecha */}
      <path
        d="M104 84 Q 140 62 168 78 Q 176 100 160 110 Q 138 106 104 96 Z"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.6"
      />
      {/* Ala inferior derecha */}
      <path
        d="M104 106 Q 134 112 154 130 Q 158 146 140 148 Q 118 142 104 122 Z"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.6"
      />
      {/* Detalles en alas */}
      <g fill={p.primary} opacity="0.85">
        <circle cx="52" cy="90" r="2.5" />
        <circle cx="148" cy="90" r="2.5" />
        <circle cx="70" cy="86" r="1.3" opacity="0.75" />
        <circle cx="130" cy="86" r="1.3" opacity="0.75" />
        <circle cx="62" cy="132" r="2" />
        <circle cx="138" cy="132" r="2" />
      </g>
      {/* Puntos en el cuerpo */}
      <g fill={p.secondary}>
        <circle cx="100" cy="82" r="1.5" />
        <circle cx="100" cy="102" r="1.5" />
        <circle cx="100" cy="122" r="1.5" />
      </g>
    </>
  );
}

function RootsSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Punto brote superior */}
      <line
        x1="100"
        y1="30"
        x2="100"
        y2="52"
        stroke={p.primary}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="100" cy="30" r="2.5" fill={p.primary} />
      {/* Tronco */}
      <path d="M96 52 Q 92 80 96 110 L 104 110 Q 108 80 104 52 Z" fill={p.primary} opacity="0.85" />
      {/* Raíces principales — 5 direcciones */}
      <g fill="none" stroke={p.primary} strokeWidth="1.5" strokeLinecap="round">
        {/* Raíz central abajo */}
        <path d="M100 110 Q 100 140 100 170" />
        {/* Raíz izquierda */}
        <path d="M96 112 Q 78 130 60 156" />
        {/* Raíz derecha */}
        <path d="M104 112 Q 122 130 140 156" />
        {/* Raíz izq-medial */}
        <path d="M98 118 Q 85 140 78 168" />
        {/* Raíz der-medial */}
        <path d="M102 118 Q 115 140 122 168" />
      </g>
      {/* Sub-raíces finas */}
      <g fill="none" stroke={p.primary} strokeWidth="0.9" strokeLinecap="round" opacity="0.75">
        <path d="M76 135 Q 68 138 62 148" />
        <path d="M124 135 Q 132 138 138 148" />
        <path d="M84 148 Q 76 152 70 162" />
        <path d="M116 148 Q 124 152 130 162" />
        <path d="M92 158 Q 88 162 86 172" />
        <path d="M108 158 Q 112 162 114 172" />
      </g>
      {/* Puntos en las puntas de raíces */}
      <g fill={p.primary} opacity="0.85">
        <circle cx="100" cy="172" r="1.5" />
        <circle cx="60" cy="158" r="1.5" />
        <circle cx="140" cy="158" r="1.5" />
        <circle cx="78" cy="168" r="1.2" />
        <circle cx="122" cy="168" r="1.2" />
      </g>
    </>
  );
}

function StarSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Rombos radiales — 4 principales grandes */}
      <path d="M100 30 L 108 92 L 100 100 L 92 92 Z" fill={p.primary} />
      <path d="M100 170 L 108 108 L 100 100 L 92 108 Z" fill={p.primary} />
      <path d="M30 100 L 92 92 L 100 100 L 92 108 Z" fill={p.primary} />
      <path d="M170 100 L 108 92 L 100 100 L 108 108 Z" fill={p.primary} />
      {/* Rombos diagonales pequeños */}
      <g fill={p.primary} opacity="0.85">
        <path d="M52 52 L 90 96 L 100 100 L 96 90 Z" />
        <path d="M148 52 L 110 96 L 100 100 L 104 90 Z" />
        <path d="M52 148 L 90 104 L 100 100 L 96 110 Z" />
        <path d="M148 148 L 110 104 L 100 100 L 104 110 Z" />
      </g>
      {/* Punto central luminoso */}
      <circle cx="100" cy="100" r="4" fill={p.secondary} />
      <circle cx="100" cy="100" r="2" fill={p.primary} />
      {/* Estrellas pequeñas orbitando */}
      <g fill={p.primary}>
        <path d="M45 88 L 46 91 L 49 92 L 46 93 L 45 96 L 44 93 L 41 92 L 44 91 Z" opacity="0.8" />
        <path
          d="M155 112 L 156 115 L 159 116 L 156 117 L 155 120 L 154 117 L 151 116 L 154 115 Z"
          opacity="0.8"
        />
        <path
          d="M112 45 L 113 48 L 116 49 L 113 50 L 112 53 L 111 50 L 108 49 L 111 48 Z"
          opacity="0.75"
        />
        <path
          d="M88 155 L 89 158 L 92 159 L 89 160 L 88 163 L 87 160 L 84 159 L 87 158 Z"
          opacity="0.75"
        />
      </g>
    </>
  );
}

function SpiralSymbol({ p }: { p: Palette }): ReactElement {
  return (
    <>
      {/* Círculo exterior */}
      <circle cx="100" cy="100" r="70" fill="none" stroke={p.primary} strokeWidth="1.4" />
      {/* Espiral superior — enrollada hacia arriba-derecha */}
      <path
        d="M100 100 Q 100 70 122 62 Q 148 60 148 82 Q 148 100 128 100 Q 116 100 116 90 Q 116 82 124 82"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* Espiral inferior — enrollada hacia abajo-izquierda */}
      <path
        d="M100 100 Q 100 130 78 138 Q 52 140 52 118 Q 52 100 72 100 Q 84 100 84 110 Q 84 118 76 118"
        fill="none"
        stroke={p.primary}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* Puntos brillantes */}
      <circle cx="128" cy="82" r="3" fill={p.primary} />
      <circle cx="72" cy="118" r="3" fill={p.primary} />
      <circle cx="122" cy="62" r="2" fill={p.primary} opacity="0.8" />
      <circle cx="78" cy="138" r="2" fill={p.primary} opacity="0.8" />
      {/* Puntos decorativos dispersos */}
      <g fill={p.primary} opacity="0.7">
        <circle cx="140" cy="140" r="1.6" />
        <circle cx="60" cy="60" r="1.6" />
        <circle cx="150" cy="120" r="1.2" />
        <circle cx="50" cy="80" r="1.2" />
      </g>
    </>
  );
}

// ---------------------------------------------------------------------------
// Componente público
// ---------------------------------------------------------------------------

const RENDER_MAP: Record<SpiritualSymbolVariant, (args: { p: Palette }) => ReactElement> = {
  eye: EyeSymbol,
  moon: MoonSymbol,
  lotus: LotusSymbol,
  sun: SunSymbol,
  orbit: OrbitSymbol,
  butterfly: ButterflySymbol,
  roots: RootsSymbol,
  star: StarSymbol,
  spiral: SpiralSymbol,
};

export function SpiritualSymbol({
  variant,
  size = 72,
  tone = 'light',
  className,
}: SpiritualSymbolProps): ReactElement {
  const p = tone === 'dark' ? PALETTE_DARK : PALETTE_LIGHT;
  const Render = RENDER_MAP[variant];
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-hidden="true"
      focusable={false}
    >
      <Render p={p} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Asignación coherente y estable de símbolo por item
// ---------------------------------------------------------------------------

/** Hash determinista simple sobre string. Devuelve entero >= 0. */
function stableHash(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

const VARIANTS: SpiritualSymbolVariant[] = [
  'eye',
  'moon',
  'lotus',
  'sun',
  'orbit',
  'butterfly',
  'roots',
  'star',
  'spiral',
];

/** Reglas de keyword → variante. Se aplican en orden; el primero que
 *  matchea gana. Mantener las reglas semánticamente coherentes con la
 *  referencia (protección/intuición/sanación/expansión/etc.). */
const KEYWORD_RULES: Array<{ re: RegExp; variant: SpiritualSymbolVariant }> = [
  { re: /(ojo|protecc|amuleto|guarda|escudo|tarot)/i, variant: 'eye' },
  { re: /(luna|noct|noche|ciclo|intuic|femin|sueñ)/i, variant: 'moon' },
  { re: /(loto|flor|sanac|sanad|calma|paz|armon|reiki)/i, variant: 'lotus' },
  { re: /(sol|expans|energ|despertar|brillo|luz|solar|chak)/i, variant: 'sun' },
  { re: /(mandala|conex|órbit|orbit|geometr|círcul|circul|univer|vínc)/i, variant: 'orbit' },
  {
    re: /(mariposa|transform|metamorf|renac|cambio|arte|creativ|pintura|process)/i,
    variant: 'butterfly',
  },
  { re: /(raíz|raices|raiz|tierra|memoria|origen|linaje|familia|ancestr)/i, variant: 'roots' },
  { re: /(estrella|guía|guia|norte|camino|akásh|akashic|regist)/i, variant: 'star' },
  { re: /(alma|espiral|profund|misteri|sombra|sonido|cuenc|frecuencia|vibra)/i, variant: 'spiral' },
];

/**
 * Devuelve una variante de `SpiritualSymbol` coherente con el item:
 *  1) Si `hintText` contiene alguna keyword mapeada → esa variante.
 *  2) Si no, fallback determinista: hash de `seed` → una de las 9 variantes.
 *
 * Estabilidad: mismo `seed` produce siempre el mismo símbolo entre renders.
 * Ejemplo: `pickSymbol('mandala-luna-llena', 'Mandala de la luna llena')`
 * detecta "luna" → `moon`. Otro caso: `pickSymbol('sello-agua', 'Sello del agua')`
 * no matchea keywords, cae a hash de 'sello-agua' → variante estable.
 */
export function pickSymbol(seed: string, hintText?: string | null): SpiritualSymbolVariant {
  const hint = `${hintText ?? ''} ${seed}`.trim();
  for (const rule of KEYWORD_RULES) {
    if (rule.re.test(hint)) return rule.variant;
  }
  return VARIANTS[stableHash(seed) % VARIANTS.length]!;
}
